import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

export default class extends Controller {
  static targets = [
    "question", "questionHeading", "answer", "questionError", "progress",
    "progressBar", "progressValue", "progressRegion", "backButton",
    "nextButton", "submitButton", "dismissButton", "completionButton", "serverError",
    "completion", "completionMessage",
  ]

  static values = {
    startUrl: String,
    dismissUrl: String,
    submitUrl: String,
    preview: { type: Boolean, default: false },
    autoOpen: { type: Boolean, default: true },
  }

  connect() {
    this.currentIndex = 0
    this.started = false
    this.completed = false
    this.allowHide = false
    this.dismissInFlight = false
    this.render()

    if (this.autoOpenValue) queueMicrotask(() => this.open())
  }

  open() {
    const Modal = window.bootstrap?.Modal
    if (!Modal) return

    this.modal = Modal.getOrCreateInstance(this.element, { keyboard: true })
    this.modal.show()
  }

  async shown() {
    this.focusCurrentQuestion()
    if (this.previewValue || this.started || !this.hasStartUrlValue) return

    this.started = true
    try {
      const response = await post(this.startUrlValue)
      if (!response.ok) this.showServerError(this.unavailableMessage())
    } catch {
      this.showServerError(this.unavailableMessage())
    }
  }

  beforeHide() {
    if (this.completed || this.allowHide || this.previewValue) return

    this.allowHide = true
    this.clearAnswers()
    void this.recordDismissal()
  }

  async requestDismiss() {
    if (this.previewValue) {
      this.allowHide = true
      this.hideModal()
      return
    }

    this.allowHide = true
    this.clearAnswers()
    this.hideModal()
    await this.recordDismissal()
  }

  async recordDismissal() {
    if (this.dismissInFlight) return

    this.dismissInFlight = true
    this.setDismissBusy(true)

    try {
      const response = await post(this.dismissUrlValue)
      if (!response.ok) throw new Error("dismiss_failed")
    } catch {
      // Closing a survey is fail-open. If this write failed, the durable
      // presentation can safely be offered again on a later page.
    } finally {
      this.dismissInFlight = false
      this.setDismissBusy(false)
    }
  }

  next() {
    if (!this.validateCurrent()) return
    if (this.currentIndex >= this.questionTargets.length - 1) return

    this.currentIndex += 1
    this.render()
    this.focusCurrentQuestion()
  }

  back() {
    if (this.currentIndex <= 0) return

    this.currentIndex -= 1
    this.render()
    this.focusCurrentQuestion()
  }

  answerChanged() {
    this.clearQuestionError(this.currentIndex)
    this.hideServerError()
  }

  async submit() {
    if (!this.validateCurrent()) return

    this.hideServerError()
    this.setSubmitBusy(true)

    if (this.previewValue) {
      this.showCompletion()
      this.setSubmitBusy(false)
      return
    }

    try {
      const response = await post(this.submitUrlValue, {
        body: JSON.stringify({ answers: this.answersPayload() }),
        contentType: "application/json",
        responseKind: "json",
      })
      const data = await this.responseJson(response)
      if (!response.ok) {
        const message = [data?.errors].flat().filter(Boolean).join(" ") || this.submitErrorMessage()
        this.showServerError(message)
        return
      }

      if (data?.completion_message && this.hasCompletionMessageTarget) {
        this.completionMessageTarget.textContent = data.completion_message
      }
      this.showCompletion()
    } catch {
      this.showServerError(this.submitErrorMessage())
    } finally {
      this.setSubmitBusy(false)
    }
  }

  closeCompleted() {
    this.completed = true
    this.allowHide = true
    this.hideModal()
  }

  keydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault()
      if (this.currentIndex === this.questionTargets.length - 1) this.submit()
      else this.next()
    }
  }

  validateCurrent() {
    const question = this.questionTargets[this.currentIndex]
    if (!question || !this.questionRequired(question)) return true
    if (this.questionAnswered(question)) return true

    // The error paragraph is a role="alert" the answer group describes; every
    // control of the question is marked invalid so whichever one is focused
    // announces it, and focus lands on the first so the fix is one gesture away.
    const error = this.questionErrorTargets[this.currentIndex]
    error?.classList.remove("d-none")
    const answers = this.answersFor(question)
    answers.forEach((answer) => answer.setAttribute("aria-invalid", "true"))
    answers[0]?.focus()
    return false
  }

  // Presence-based on purpose (#13332): the partial renders "true" / "false",
  // but HAML renders a boolean `true` as a BARE `data-required` attribute whose
  // dataset value is "" — a strict `=== "true"` comparison read that as
  // optional and let Next advance past every unanswered required question.
  questionRequired(question) {
    const flag = question.dataset.required
    return flag !== undefined && flag !== "false"
  }

  questionAnswered(question) {
    const type = question.dataset.questionType
    const answers = this.answersFor(question)
    if (type === "multiple_choice") return answers.some((answer) => answer.checked)
    if (["rating_1_to_5", "yes_no", "single_choice"].includes(type)) {
      return answers.some((answer) => answer.checked)
    }
    return (answers[0]?.value || "").trim().length > 0
  }

  answersPayload() {
    return this.questionTargets.flatMap((question) => {
      const value = this.valueFor(question)
      if (value === null || value === "" || (Array.isArray(value) && value.length === 0)) return []
      return [{ question_id: question.dataset.questionId, value }]
    })
  }

  valueFor(question) {
    const type = question.dataset.questionType
    const answers = this.answersFor(question)
    if (type === "multiple_choice") return answers.filter((answer) => answer.checked).map((answer) => answer.value)
    if (["rating_1_to_5", "yes_no", "single_choice"].includes(type)) {
      return answers.find((answer) => answer.checked)?.value || null
    }
    return answers[0]?.value || ""
  }

  answersFor(question) {
    return Array.from(question.querySelectorAll('[data-survey-modal-target~="answer"]'))
  }

  clearAnswers() {
    this.answerTargets.forEach((answer) => {
      if (["checkbox", "radio"].includes(answer.type)) answer.checked = false
      else answer.value = ""
    })
  }

  clearQuestionError(index) {
    const error = this.questionErrorTargets[index]
    error?.classList.add("d-none")
    this.answersFor(this.questionTargets[index] || document.createElement("div"))
      .forEach((answer) => answer.removeAttribute("aria-invalid"))
  }

  render() {
    this.questionTargets.forEach((question, index) => {
      const active = index === this.currentIndex
      question.classList.toggle("d-none", !active)
      question.setAttribute("aria-hidden", String(!active))
    })

    const total = this.questionTargets.length
    const current = Math.min(this.currentIndex + 1, total)
    if (this.hasProgressTarget) {
      const template = this.progressTemplate()
      this.progressTarget.textContent = template
        .replace("%{current}", String(current))
        .replace("%{total}", String(total))
    }
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.setAttribute("aria-valuenow", String(current))
      this.progressBarTarget.setAttribute("aria-valuemax", String(total))
    }
    if (this.hasProgressValueTarget) {
      this.progressValueTarget.style.width = total > 0 ? `${(current / total) * 100}%` : "0%"
    }

    this.backButtonTarget?.classList.toggle("d-none", this.currentIndex === 0)
    const onLastQuestion = this.currentIndex === total - 1
    this.nextButtonTarget?.classList.toggle("d-none", onLastQuestion)
    this.submitButtonTarget?.classList.toggle("d-none", !onLastQuestion)
  }

  progressTemplate() {
    return this.element.dataset.surveyProgressLabel || this.progressTarget?.dataset.template || "Question %{current} of %{total}"
  }

  showCompletion() {
    this.completed = true
    this.questionTargets.forEach((question) => {
      question.classList.add("d-none")
      question.setAttribute("aria-hidden", "true")
    })
    this.progressRegionTarget?.classList.add("d-none")
    // The footer's actions are direct children of the shared footer (MODAL-2),
    // so completion swaps the visible set: the navigation actions hide and the
    // completion action becomes the one visible primary.
    ;[this.dismissButtonTarget, this.backButtonTarget, this.nextButtonTarget, this.submitButtonTarget]
      .forEach((button) => button?.classList.add("d-none"))
    this.completionButtonTarget?.classList.remove("d-none")
    this.completionTarget?.classList.remove("d-none")
    this.completionTarget?.focus()
  }

  focusCurrentQuestion() {
    const heading = this.questionHeadingTargets[this.currentIndex]
    const question = this.questionTargets[this.currentIndex]
    const firstAnswer = question ? this.answersFor(question)[0] : null
    ;(firstAnswer || heading)?.focus()
  }

  hideModal() {
    const Modal = window.bootstrap?.Modal
    if (!Modal) return
    const instance = this.modal || Modal.getInstance(this.element) || Modal.getOrCreateInstance(this.element)
    instance.hide()
  }

  setDismissBusy(busy) {
    if (!this.hasDismissButtonTarget) return
    this.dismissButtonTarget.disabled = busy
    this.dismissButtonTarget.setAttribute("aria-busy", String(busy))
  }

  setSubmitBusy(busy) {
    if (!this.hasSubmitButtonTarget) return
    this.submitButtonTarget.disabled = busy
    this.submitButtonTarget.setAttribute("aria-busy", String(busy))
  }

  showServerError(message) {
    if (!this.hasServerErrorTarget) return
    this.serverErrorTarget.textContent = message
    this.serverErrorTarget.classList.remove("d-none")
    this.serverErrorTarget.focus?.()
  }

  hideServerError() {
    if (!this.hasServerErrorTarget) return
    this.serverErrorTarget.textContent = ""
    this.serverErrorTarget.classList.add("d-none")
  }

  async responseJson(response) {
    try {
      return await response.json
    } catch {
      return null
    }
  }

  unavailableMessage() {
    return "This survey is no longer available. Close it and continue working."
  }


  submitErrorMessage() {
    return "We could not submit your answers. Please try again."
  }
}
